import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiSearch, FiRefreshCw } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const RestaurantMenu = () => {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [menuItems, setMenuItems] = useState([]);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchRestaurantDetails();
    fetchMenu();
  }, [restaurantId]);

  const fetchRestaurantDetails = async () => {
    try {
      const response = await api.get(`/vendor-owner/restaurant/${restaurantId}`);
      if (response.data.success) {
        setRestaurant(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch restaurant details');
    }
  };

  const fetchMenu = async () => {
    try {
      const response = await api.get(`/vendor-owner/restaurant/${restaurantId}/menu`);
      if (response.data.success) {
        setMenuItems(response.data.data);
        toast.success('Menu data refreshed successfully!');
      }
    } catch (error) {
      toast.error('Failed to fetch menu');
    } finally {
      setLoading(false);
    }
  };

  const filteredMenu = menuItems.filter(item =>
    item.name.toLowerCase().includes(search.toLowerCase()) ||
    item.categoryId?.name.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-500 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading menu...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 md:p-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <button 
              className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
              onClick={() => navigate('/vendor-owner/restaurants')}
            >
              <FiArrowLeft size={16} />
              Back
            </button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                {restaurant?.restaurantDetails?.restaurantName} - Menu
              </h1>
              <p className="text-gray-600">View your restaurant's menu items</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button 
              className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
              onClick={fetchMenu}
            >
              <FiRefreshCw size={16} />
              Refresh
            </button>
            {/* <button 
              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
              onClick={() => navigate(`/vendor-owner/restaurant/${restaurantId}/menu/add`)}
            >
              <FiPlus size={16} />
              Add Item
            </button> */}
          </div>
        </div>
        
        {/* Stats */}
        <div className="flex gap-4 mt-6">
          <div className="bg-indigo-50 rounded-xl px-4 py-2">
            <span className="text-2xl font-bold text-indigo-600">{menuItems.length}</span>
            <span className="text-sm text-indigo-700 ml-2">Total Items</span>
          </div>
          <div className="bg-green-50 rounded-xl px-4 py-2">
            <span className="text-2xl font-bold text-green-600">{menuItems.filter(item => item.isAvailable).length}</span>
            <span className="text-sm text-green-700 ml-2">Available</span>
          </div>
          <div className="bg-orange-50 rounded-xl px-4 py-2">
            <span className="text-2xl font-bold text-orange-600">{menuItems.filter(item => !item.isAvailable).length}</span>
            <span className="text-sm text-orange-700 ml-2">Unavailable</span>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search menu items..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Menu Grid */}
      {filteredMenu.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
          <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
            <FiSearch className="text-gray-400 text-3xl" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">No menu items found</h3>
          <p className="text-gray-600">
            {search ? 'Try adjusting your search terms.' : 'No menu items available for this restaurant.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredMenu.map((item) => (
            <div key={item._id} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-200 overflow-hidden">
              {/* Image */}
              {item.images ? (
                <div className="h-48 bg-gray-100 relative">
                  <img 
                    src={item.images} 
                    alt={item.name} 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="absolute top-2 right-2">
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                      item.isAvailable ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {item.isAvailable ? 'Available' : 'Unavailable'}
                    </span>
                  </div>
                  <div className="absolute inset-0 bg-gray-100 flex items-center justify-center" style={{display: 'none'}}>
                    <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                      <span className="text-gray-400 text-2xl font-bold">Rs</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-48 bg-gray-100 flex items-center justify-center relative">
                  <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                    <span className="text-gray-400 text-2xl font-bold">Rs</span>
                  </div>
                  <div className="absolute top-2 right-2">
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                      item.isAvailable ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {item.isAvailable ? 'Available' : 'Unavailable'}
                    </span>
                  </div>
                </div>
              )}

              {/* Content */}
              <div className="p-6">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.name}</h3>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs font-medium">
                      {item.categoryId?.name || 'Uncategorized'}
                    </span>
                  </div>
                  {item.description && (
                    <p className="text-sm text-gray-600 line-clamp-2">{item.description}</p>
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-center pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-1">
                    <span className="text-gray-400 text-sm">Rs</span>
                    <span className="text-xl font-bold text-gray-900">{item.price}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RestaurantMenu;
